import React, { useEffect, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { ContainedButton, OutlinedButton } from '../Buttons';
import {
  applyUserSettingsByScope,
  type EmailNotificationSettings,
  getScopePolicySettings,
  getUserSettingsByUserId,
} from '../../../api/usersApi';
import { RolesLowerCase } from '../../../enums/ROLES';
import { isMobilePreferencesEnabled } from '../../../helpers';
import Modal from '../Modal';
import styles from './ApplyPreferencesModal.module.scss';

type PreferencesScopeRole =
  | RolesLowerCase.ORGANIZATION_ADMIN
  | RolesLowerCase.ANALYST
  | RolesLowerCase.VIEWER;

const rolePreferenceTargets: { label: string; role: PreferencesScopeRole; className: string }[] = [
  { label: 'Org Admins', role: RolesLowerCase.ORGANIZATION_ADMIN, className: styles.orgAdminTarget },
  { label: 'Analysts', role: RolesLowerCase.ANALYST, className: styles.analystTarget },
  { label: 'Viewers', role: RolesLowerCase.VIEWER, className: styles.viewerTarget },
];

const defaultEmailSettings: EmailNotificationSettings = {
  shared_content_email_notifications_enabled: true,
  moved_content_email_notifications_enabled: true,
};

type TApplyPreferencesModal = {
  open: boolean;
  onClose: () => void;
  title: string;
  fixedScope?: 'group' | 'user';
  fixedGroupId?: number;
  fixedUserId?: number;
  allowOrgAndRole?: boolean;
};

const ApplyPreferencesModal: React.FC<TApplyPreferencesModal> = ({
  open,
  onClose,
  title,
  fixedScope,
  fixedGroupId,
  fixedUserId,
  allowOrgAndRole,
}) => {
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState<'org' | 'role'>(allowOrgAndRole ? 'org' : 'org');
  const [role, setRole] = useState<PreferencesScopeRole>(RolesLowerCase.ANALYST);
  const [platform, setPlatform] = useState<'web' | 'mobile'>('web');
  const [muteAllVideos, setMuteAllVideos] = useState(false);
  const [sendClipsAddOnBottom, setSendClipsAddOnBottom] = useState(true);
  const [clipDefaultInSeconds, setClipDefaultInSeconds] = useState('5');
  const [clipDefaultOutSeconds, setClipDefaultOutSeconds] = useState('5');
  const [playingArrowStepSeconds, setPlayingArrowStepSeconds] = useState('5');
  const [emailSettings, setEmailSettings] = useState(defaultEmailSettings);
  const [loadedUserSettings, setLoadedUserSettings] = useState<{
    web: any;
    mobile: any;
    email: EmailNotificationSettings;
  } | null>(null);
  const loadedEmailPolicyTarget = useRef('');
  const showMobilePreferences = isMobilePreferencesEnabled();
  const applyToForm = (settings: any) => {
    if (!settings) return;
    setMuteAllVideos(!!settings.muteAllVideos);
    setSendClipsAddOnBottom(typeof settings.sendClipsAddOnBottom === 'boolean' ? settings.sendClipsAddOnBottom : true);
    setClipDefaultInSeconds(`${settings.clipDefaultInSeconds ?? 5}`);
    setClipDefaultOutSeconds(`${settings.clipDefaultOutSeconds ?? 5}`);
    setPlayingArrowStepSeconds(`${settings.playingArrowStepSeconds ?? 5}`);
  };

  useEffect(() => {
    if (!showMobilePreferences && platform === 'mobile') {
      setPlatform('web');
    }
  }, [showMobilePreferences, platform]);

  useEffect(() => {
    if (!open) return;
    if (fixedScope !== 'user' || !fixedUserId) return;
    setLoading(true);
    getUserSettingsByUserId(fixedUserId)
      .then((resp) => {
        setLoadedUserSettings(resp);
        applyToForm(platform === 'web' ? resp?.web : resp?.mobile);
        setEmailSettings(resp?.email || defaultEmailSettings);
      })
      .finally(() => setLoading(false));
  }, [open, fixedScope, fixedUserId]);

  useEffect(() => {
    if (!open) return;
    if (fixedScope !== 'user') return;
    if (!loadedUserSettings) return;
    applyToForm(platform === 'web' ? loadedUserSettings.web : loadedUserSettings.mobile);
  }, [platform, loadedUserSettings, open, fixedScope]);

  useEffect(() => {
    if (!open) {
      loadedEmailPolicyTarget.current = '';
      return;
    }
    if (fixedScope === 'user') return;

    const loadPolicy = async () => {
      setLoading(true);
      try {
        if (fixedScope === 'group' && fixedGroupId) {
          const emailTarget = `group-${fixedGroupId}`;
          const shouldLoadEmail = loadedEmailPolicyTarget.current !== emailTarget;
          const [preferences, email] = await Promise.all([
            getScopePolicySettings({ scope: 'group', platform, groupId: fixedGroupId }),
            shouldLoadEmail
              ? getScopePolicySettings({ scope: 'group', platform: 'email', groupId: fixedGroupId })
              : Promise.resolve(null),
          ]);
          applyToForm(preferences);
          if (email) {
            setEmailSettings(email);
            loadedEmailPolicyTarget.current = emailTarget;
          }
          return;
        }

        if (!fixedScope && allowOrgAndRole) {
          const policyScope = scope === 'role' ? 'role' : 'org';
          const policyRole = scope === 'role' ? role : undefined;
          const emailTarget = `${policyScope}-${policyRole || 'all'}`;
          const shouldLoadEmail = loadedEmailPolicyTarget.current !== emailTarget;
          const [preferences, email] = await Promise.all([
            getScopePolicySettings({ scope: policyScope, platform, role: policyRole }),
            shouldLoadEmail
              ? getScopePolicySettings({ scope: policyScope, platform: 'email', role: policyRole })
              : Promise.resolve(null),
          ]);
          applyToForm(preferences);
          if (email) {
            setEmailSettings(email);
            loadedEmailPolicyTarget.current = emailTarget;
          }
          return;
        }
      } finally {
        setLoading(false);
      }
    };

    void loadPolicy();
  }, [open, fixedScope, fixedGroupId, allowOrgAndRole, scope, role, platform]);

  const submit = async () => {
    const inVal = Math.max(0, Number(clipDefaultInSeconds) || 0);
    const outVal = Math.max(0, Number(clipDefaultOutSeconds) || 0);
    const stepVal = Number(playingArrowStepSeconds);
    if (inVal + outVal < 1 || !Number.isFinite(stepVal) || stepVal <= 0) return;

    const payload: any = {
      scope: fixedScope ? fixedScope : scope,
      platform,
      settings: {
        muteAllVideos,
        sendClipsAddOnBottom,
        clipDefaultInSeconds: inVal,
        clipDefaultOutSeconds: outVal,
        playingArrowStepSeconds: stepVal,
      },
    };
    if (fixedScope === 'group' && fixedGroupId) payload.groupId = fixedGroupId;
    if (fixedScope === 'user' && fixedUserId) payload.userId = fixedUserId;
    if (!fixedScope && scope === 'role') payload.role = role;

    const emailPayload = {
      ...payload,
      platform: 'email' as const,
      settings: emailSettings,
    };

    setLoading(true);
    try {
      await Promise.all([
        applyUserSettingsByScope(payload),
        applyUserSettingsByScope(emailPayload),
      ]);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.title}>{title}</div>
        {!fixedScope && allowOrgAndRole && (
          <div className={styles.targetPanel}>
            <div className={styles.targetHeader}>
              <span>Apply preferences to</span>
              <strong className={scope === 'org' ? '' : rolePreferenceTargets.find((target) => target.role === role)?.className}>
                {scope === 'org' ? 'Organisation' : rolePreferenceTargets.find((target) => target.role === role)?.label}
              </strong>
            </div>
            <div className={styles.targetModeTabs}>
              <button onClick={() => setScope('org')} className={scope === 'org' ? styles.active : ''} type="button">
                Organisation-wide
              </button>
              <button onClick={() => setScope('role')} className={scope === 'role' ? styles.active : ''} type="button">
                Role-specific
              </button>
            </div>
            {scope === 'role' && (
              <div className={styles.rolePills}>
                {rolePreferenceTargets.map((target) => (
                  <button
                    key={target.role}
                    onClick={() => setRole(target.role)}
                    className={`${target.className} ${role === target.role ? styles.active : ''}`}
                    type="button"
                  >
                    {target.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {showMobilePreferences && (
          <div className={styles.section}>
            <div className={styles.label}>Platform</div>
            <div className={styles.segmented}>
              <button type="button" className={platform === 'web' ? styles.active : ''} onClick={() => setPlatform('web')}>
                Web
              </button>
              <button type="button" className={platform === 'mobile' ? styles.active : ''} onClick={() => setPlatform('mobile')}>
                Mobile
              </button>
            </div>
          </div>
        )}
        <div className={`${styles.section} ${styles.emailSection}`}>
          <div className={styles.label}>Email notifications</div>
          <div className={styles.hint}>
            Apply these email preferences to the selected users, including future users matching this selection.
          </div>
          <div className={styles.emailOptions}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={emailSettings.shared_content_email_notifications_enabled}
                onChange={(event) =>
                  setEmailSettings((previous) => ({
                    ...previous,
                    shared_content_email_notifications_enabled: event.target.checked,
                  }))
                }
              />
              <span>Shared content</span>
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={emailSettings.moved_content_email_notifications_enabled}
                onChange={(event) =>
                  setEmailSettings((previous) => ({
                    ...previous,
                    moved_content_email_notifications_enabled: event.target.checked,
                  }))
                }
              />
              <span>Moved shared and collaborative content</span>
            </label>
          </div>
        </div>
        <label className={styles.checkboxRow}>
          <input type="checkbox" checked={muteAllVideos} onChange={(e) => setMuteAllVideos(e.target.checked)} />
          <span>Mute all Videos</span>
        </label>
        <div className={styles.section}>
          <div className={styles.label}>Send Clips default position</div>
          <div className={styles.segmented}>
            <button
              type="button"
              className={!sendClipsAddOnBottom ? styles.active : ''}
              onClick={() => setSendClipsAddOnBottom(false)}
            >
              Top
            </button>
            <button
              type="button"
              className={sendClipsAddOnBottom ? styles.active : ''}
              onClick={() => setSendClipsAddOnBottom(true)}
            >
              Bottom
            </button>
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.label}>Left/Right jump while playing</div>
          <div className={styles.singleInput}>
            <label>
              <span>Jump seconds</span>
              <div className={styles.inputWithUnit}>
                <input
                  value={playingArrowStepSeconds}
                  onChange={(e) => setPlayingArrowStepSeconds(e.target.value)}
                  type="number"
                  min="0.1"
                  step="0.5"
                />
                <span>sec</span>
              </div>
            </label>
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.label}>New clip default range</div>
          <div className={styles.timeGrid}>
            <label>
              <span>Before point</span>
              <div className={styles.inputWithUnit}>
                <input
                  value={clipDefaultInSeconds}
                  onChange={(e) => setClipDefaultInSeconds(e.target.value)}
                  type="number"
                  min="0"
                  step="0.5"
                />
                <span>sec</span>
              </div>
            </label>
            <label>
              <span>After point</span>
              <div className={styles.inputWithUnit}>
                <input
                  value={clipDefaultOutSeconds}
                  onChange={(e) => setClipDefaultOutSeconds(e.target.value)}
                  type="number"
                  min="0"
                  step="0.5"
                />
                <span>sec</span>
              </div>
            </label>
          </div>
        </div>
        <div className={styles.actions}>
          <ContainedButton onClick={submit}>Apply</ContainedButton>
          <OutlinedButton kind="default" onClick={onClose}>
            Cancel
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default ApplyPreferencesModal;
